import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import styled from "styled-components";
import axios from "axios";
import moment from "moment";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Legend,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Legend,
  Tooltip,
  ChartDataLabels
);
const Container = styled.div`
  background-color: #fff;
  height: 100%;
  padding: 1rem;
  // box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;
  width: 100%;
  border-radius: 20px;
`;
const Wrapper = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  width: 100%;
  max-width: 100%;
  margin: 0.8rem auto;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  border: 1px solid #e3f2fd;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
    margin: 0.5rem auto;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    border-radius: 8px;
    margin: 0.4rem auto;
  }
`;

const LoadingSpinner = styled.div`
  color: #333;
  font-size: 0.95rem;
  font-weight: 600;

  @media (max-width: 768px) {
    padding: 1.5rem;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.85rem;
  }
`;

const HeaderSection = styled.div`
  margin-bottom: 1rem;

  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
  }
`;

const Highlight = styled.h3`
  color: #004080;
  margin-bottom: 0.5rem;
  margin-top: 0;
  font-size: 1.25rem;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.4rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 0.3rem;
  }
`;

const SubTitle = styled.h5`
  margin: 0;
  font-size: 0.95rem;
  color: #555;
  font-weight: normal;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const SummarySection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    gap: 0.8rem;
    margin-top: 1.25rem;
  }

  @media (max-width: 480px) {
    gap: 0.6rem;
    margin-top: 1rem;
  }
`;

export const SummaryCard = styled.div`
  flex: 1;
  min-width: 140px;
  background-color: ${(props) => props.bgColor || "#fff"};
  padding: 0.75rem 0.5rem;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  max-width: 180px;
  transition: all 0.3s ease;

  @media (max-width: 1024px) {
    padding: 0.7rem 0.45rem;
    max-width: 160px;
  }

  @media (max-width: 768px) {
    min-width: 120px;
    max-width: 140px;
    padding: 0.65rem 0.4rem;
  }

  @media (max-width: 480px) {
    min-width: 100px;
    max-width: 120px;
    padding: 0.6rem 0.35rem;
  }
`;

export const CardTitle = styled.h4`
  font-size: 0.75rem;
  color: #555;
  margin-bottom: 0.4rem;
  margin-top: 0;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 0.7rem;
    margin-bottom: 0.35rem;
  }

  @media (max-width: 480px) {
    font-size: 0.65rem;
    margin-bottom: 0.3rem;
  }
`;

export const CardValue = styled.h3`
  font-size: 1.1rem;
  font-weight: bold;
  color: ${(props) => props.color || "#000"};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const TimelineWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-top: 2rem;
  overflow-x: auto;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
  }

  @media (max-width: 480px) {
    margin-top: 1rem;
  }
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0.2rem 0;
  min-height: 35px;
  gap: 0.5rem;

  @media (max-width: 768px) {
    margin: 0.15rem 0;
    min-height: 32px;
    gap: 0.4rem;
  }

  @media (max-width: 480px) {
    margin: 0.1rem 0;
    min-height: 28px;
    gap: 0.3rem;
  }
`;

const TimeLabel = styled.span`
  width: 80px;
  font-size: 0.8rem;
  // color: #64748b;
    color:rgb(0, 2, 5);
  font-weight: 900;
  text-align: ${(props) => (props.alignRight ? "right" : "left")};
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 70px;
    font-size: 0.75rem;
  }

  @media (max-width: 480px) {
    width: 60px;
    font-size: 0.7rem;
  }
`;

const ChartContainer = styled.div`
  flex: 1;
  height: 40px;
  position: relative;
  min-width: 0;

  @media (max-width: 768px) {
    height: 35px;
  }

  @media (max-width: 480px) {
    height: 30px;
  }
`;

const MotorOnOffRuntimeTableSmall = forwardRef(
  ({ motor_id, startDate, endDate, token, location_name }, ref) => {
    const exportRef = useRef();
    const [periods, setPeriods] = useState([]);
    const [totalRuntime, setTotalRuntime] = useState("");
    const [energyConsumed, setEnergyConsumed] = useState("");
    const [totalCost, setTotalCost] = useState("");
    const [loading, setLoading] = useState(true);
    const [unitRate, setUnitRate] = useState(7.35);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useImperativeHandle(ref, () => ({
      getElement: () => exportRef.current,
    }));

    useEffect(() => {
      const handleResize = () => setWindowWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    const parseDuration = (durationStr) => {
      if (!durationStr) return 0;
      const hourMatch = durationStr.match(/(\d+)\s*hr/);
      const minMatch = durationStr.match(/(\d+)\s*min/);
      const hours = hourMatch ? parseInt(hourMatch[1]) : 0;
      const mins = minMatch ? parseInt(minMatch[1]) : 0;
      return hours * 60 + mins;
    };

    useEffect(() => {
      async function fetchData() {
        if (!motor_id || !startDate || !endDate) return;
        setLoading(true);
        try {
          const org_id = localStorage.getItem("org_id");

          const month = moment(startDate).format("MM");
          const year = moment(startDate).format("YYYY");

          let dynamicRate = null;

          if (org_id) {
            try {
              const rateRes = await axios.get(
                `${process.env.REACT_APP_API}/user/unitrate/monthly/${org_id}/${year}/${month}`,
                { headers: { Authorization: token[0] } }
              );
              dynamicRate = rateRes?.data?.data?.current_unit_rate;
            } catch (monthErr) {
              console.warn("Month-specific rate not found, using latest instead.");
              try {
                const rateRes = await axios.get(
                  `${process.env.REACT_APP_API}/user/unitrate/${org_id}/latest`,
                  { headers: { Authorization: token[0] } }
                );
                dynamicRate = rateRes?.data?.data?.current_unit_rate;
              } catch {
                console.warn("Failed to fetch even latest rate, using default 7.35");
              }
            }

            if (dynamicRate && !isNaN(dynamicRate)) {
              setUnitRate(dynamicRate);
            }
          }

          const url = `${process.env.REACT_APP_API}/sensor/motor-runtime?motor_id=${motor_id}&start_date=${startDate}&end_date=${endDate}`;
          const res = await axios.get(url, {
            headers: { Authorization: token[0] },
          });

          const onlinePeriods = res.data.online_periods || [];
          const totalRuntimeValue = res.data.total_run_time || "0 mins";
          const energyValue = parseFloat(res.data.energy_consumed_kwh || 0).toFixed(2);
          const costValue = (energyValue * (dynamicRate || unitRate)).toFixed(2);

          setPeriods(onlinePeriods);
          setTotalRuntime(totalRuntimeValue);
          setEnergyConsumed(energyValue);
          setTotalCost(costValue);
        } catch (err) {
          console.error("❌ Error fetching runtime data:", err);
        } finally {
          setLoading(false);
        }
      }

      fetchData();
    }, [motor_id, startDate, endDate, token]);

    if (loading)
      return (
        <Wrapper>
          <LoadingSpinner>Loading runtime data...</LoadingSpinner>
        </Wrapper>
      );

    if (periods.length === 0)
      return (
        <Container>
          <LoadingSpinner>No runtime data available for this date.</LoadingSpinner>
        </Container>
      );

    const maxDuration = Math.max(
      ...periods.map((item) => parseDuration(item.duration)),
      1
    );

    const colors = [
      "rgba(99, 155, 245, 0.7)",
      'rgba(163, 199, 244, 0.7)',
    ];

    // return (
    //   <Wrapper ref={exportRef}>
    //     <HeaderSection>
    //       <Highlight>{location_name}</Highlight>
    //       <SubTitle>Motor Runtime of {moment(startDate).format("MMM DD, YYYY")}</SubTitle>
    //     </HeaderSection>

    //     <TimelineWrapper>
    //       {periods.map((p, i) => {
    //         const duration = parseDuration(p.duration);

    //         return (
    //           <TimeRow key={i}>
    //             <TimeLabel>{moment(p.start_time, "HH:mm:ss").format("h:mm A")}</TimeLabel>

    //             <ChartContainer>
    //               <Bar
    //                 data={{
    //                   labels: [`Run ${i + 1}`],
    //                   // datasets: [
    //                   //   {
    //                   //     label: "Runtime (mins)",
    //                   //     data: [displayDuration],
    //                   //     backgroundColor: colors[i % colors.length],
    //                   //     borderRadius: 6,
    //                   //     barThickness: 28,
    //                   //   },
    //                   // ],
    //                   datasets: [
    //                     {
    //                       label: "Runtime (mins)",
    //                       data: [duration],          // ← use duration directly, not displayDuration
    //                       backgroundColor: colors[i % colors.length],
    //                       borderRadius: 6,
    //                       barThickness: 28,
    //                     },
    //                   ],

    //                 }}
    //                 options={{
    //                   indexAxis: "y",
    //                   maintainAspectRatio: false,
    //                   responsive: true,
    //                   plugins: {
    //                     legend: { display: false },
    //                     tooltip: { enabled: false },
    //                     datalabels: {
    //                       color: "rgba(0, 0, 0, 0.8)",
    //                       font: {
    //                         family: "Arial Black, Poppins, sans-serif",
    //                         size: windowWidth <= 480 ? 11 : (windowWidth <= 768 ? 13 : 14),
    //                         weight: 900
    //                       },
    //                       anchor: "end",   // ← only once
    //                       align: "end",    // ← only once, no duplicate below
    //                       formatter: () => p.duration || "0 min",
    //                     },
    //                   },
    //                   // scales: {
    //                   //   x: {
    //                   //     beginAtZero: true,
    //                   //     display: false,
    //                   //     min: 0,
    //                   //     max: maxDuration + (maxDuration * 0.1),
    //                   //   },
    //                   //   y: { display: false },
    //                   // },
    //                   scales: {
    //                     x: {
    //                       beginAtZero: true,
    //                       display: false,
    //                       min: 0,
    //                       max: maxDuration,          // ← remove the 0.35 padding, handle space via layout
    //                     },
    //                     y: { display: false },
    //                   },
    //                   layout: {
    //                     padding: {
    //                       top: 0,
    //                       bottom: 0,
    //                       right: 70,                 // ← gap after bar for duration text
    //                     }
    //                   },
    //                 }}
    //               />
    //             </ChartContainer>

    //             <TimeLabel alignRight>
    //               {moment(p.end_time, "HH:mm:ss").format("h:mm A")}
    //             </TimeLabel>
    //           </TimeRow>

    //         );
    //       })}
    //     </TimelineWrapper>

    //     {/* Summary Section */}
    //     <SummarySection>
    //       <SummaryCard bgColor="#e3f2fd">
    //         <CardTitle>Total Runtime</CardTitle>
    //         <CardValue color="#1e3a8a">{totalRuntime}</CardValue>
    //       </SummaryCard>

    //       <SummaryCard bgColor="#fff3e0">
    //         <CardTitle>Power Consumed</CardTitle>
    //         <CardValue color="#e65100">{energyConsumed} kWh</CardValue>
    //       </SummaryCard>

    //       {/* {moment(startDate).isSame(moment(), "day") &&
    //         moment(endDate).isSame(moment(), "day") && (
    //           <SummaryCard bgColor="#e8f5e9">
    //             <CardTitle>Total Cost</CardTitle>
    //             <CardValue color="#2e7d32">₹ {totalCost}</CardValue>
    //           </SummaryCard>
    //         )} */}

    //       <SummaryCard bgColor="#e8f5e9">
    //         <CardTitle>Total Cost</CardTitle>
    //         <CardValue color="#2e7d32">₹ {totalCost}</CardValue>
    //       </SummaryCard>
    //     </SummarySection>
    //   </Wrapper>
    // );

    const SCALE_MAX = maxDuration * 1.5;

return (
  <Wrapper ref={exportRef}>
    <HeaderSection>
      <Highlight>{location_name}</Highlight>
      <SubTitle>Motor Runtime of {moment(startDate).format("MMM DD, YYYY")}</SubTitle>
    </HeaderSection>

    <TimelineWrapper>
      {periods.map((p, i) => {
        const duration = parseDuration(p.duration);

        return (
          <TimeRow key={i}>
            <TimeLabel>{moment(p.start_time, "HH:mm:ss").format("h:mm A")}</TimeLabel>

            <ChartContainer>
              <Bar
                data={{
                  labels: [`Run ${i + 1}`],
                  datasets: [
                    {
                      label: "Runtime (mins)",
                      data: [duration],
                      backgroundColor: colors[i % colors.length],
                      borderRadius: 6,
                      barThickness: 26,
                    },
                  ],
                }}
                options={{
                  indexAxis: "y",
                  maintainAspectRatio: false,
                  responsive: true,
                  plugins: {
                    legend: { display: false },
                    tooltip: { enabled: false },
                    datalabels: {
                      color: "rgba(0, 0, 0, 0.8)",
                      font: {
                        family: "Arial Black, Poppins, sans-serif",
                        size: windowWidth <= 480 ? 10 : (windowWidth <= 768 ? 12 : 13),
                        weight: 900,
                      },
                      anchor: "end",
                      align: "end",
                      clip: false,
                      formatter: () => p.duration || "0 min",
                    },
                  },
                  scales: {
                    x: {
                      beginAtZero: true,
                      display: false,
                      min: 0,
                      max: SCALE_MAX,
                    },
                    y: { display: false },
                  },
                  layout: {
                    padding: { top: 0, bottom: 0, right: 90 },
                  },
                }}
              />
            </ChartContainer>

            <TimeLabel alignRight>
              {moment(p.end_time, "HH:mm:ss").format("h:mm A")}
            </TimeLabel>
          </TimeRow>
        );
      })}
    </TimelineWrapper>

    <SummarySection>
      <SummaryCard bgColor="#e3f2fd">
        <CardTitle>Total Runtime</CardTitle>
        <CardValue color="#1e3a8a">{totalRuntime}</CardValue>
      </SummaryCard>
      <SummaryCard bgColor="#fff3e0">
        <CardTitle>Power Consumed</CardTitle>
        <CardValue color="#e65100">{energyConsumed} kWh</CardValue>
      </SummaryCard>
      <SummaryCard bgColor="#e8f5e9">
        <CardTitle>Total Cost</CardTitle>
        <CardValue color="#2e7d32">₹ {totalCost}</CardValue>
      </SummaryCard>
    </SummarySection>
  </Wrapper>
);
  }
);

export default MotorOnOffRuntimeTableSmall;
